import { FunctionComponent } from 'react';
import { BadgeCard } from './BadgeCard';
import { css } from '@compiled/react';
import { useLazyLoadQuery } from 'react-relay';
import { ProfileBadgesQuery } from 'graphql/__generated__/ProfileBadgesQuery.graphql';
import { PROFILE_BADGES_QUERY } from 'graphql/ProfileBadgesQuery';
import {
  BadgesEdge,
  BadgesEdges,
  UnifiedProfileForBadges,
} from 'constants/types';
import { usePathParam, Link } from 'react-resource-router';
import MainPanelSectionHeader from 'components/common/section/MainPanelSectionHeader';

import { token } from '@atlaskit/tokens';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import { ProfileHeaderQuery } from 'graphql/__generated__/ProfileHeaderQuery.graphql';
import { PROFILE_HEADER_QUERY } from 'graphql/ProfileHeaderQuery';
import { MOBILE, DESKTOP } from '../../../../constants/breakpoints';
import {
  trackBadgesBackToProfileClick,
  trackBadgesScreenView,
} from 'analytics/badgesAnalytics';
import useIsPersonalView from 'utils/useIsPersonalView';
import { hasProfileOwnerConsented } from 'utils';
import { useSendScreenEventEffect } from 'hooks/useScreenEvent';

const BadgesFullList: FunctionComponent = () => {
  const [pathProfileId] = usePathParam('pathProfileId') as unknown as string;

  const badgesResponse = useLazyLoadQuery<ProfileBadgesQuery>(
    PROFILE_BADGES_QUERY,
    { internalId: pathProfileId },
  ).unified?.unifiedProfile as UnifiedProfileForBadges;
  const forumsBadgesEdges =
    (badgesResponse?.badges?.edges as BadgesEdges) || [];
  const recentCoursesBadgesEdges =
    (badgesResponse?.learning?.recentCoursesBadges?.edges as BadgesEdges) || [];

  const forumsBadges = forumsBadgesEdges.map((edge: BadgesEdge) => ({
    ...(edge?.node ?? {}),
    src: edge?.node?.imageUrl,
  }));
  const recentCoursesBadges = recentCoursesBadgesEdges.map(
    (edge: BadgesEdge) => ({
      ...(edge?.node ?? {}),
      src: edge?.node?.imageUrl,
    }),
  );

  const profileResponseData = useLazyLoadQuery<ProfileHeaderQuery>(
    PROFILE_HEADER_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );
  const profileData = profileResponseData?.unified?.unifiedProfile;

  const isPersonalView = useIsPersonalView(pathProfileId);
  const hasProfileOwnerConsent = hasProfileOwnerConsented(
    badgesResponse?.isProfileOwnerConsented ?? undefined,
  );

  useSendScreenEventEffect(() => trackBadgesScreenView(isPersonalView), {
    shouldSendEvent: Boolean(profileData),
  });

  if (!profileData) {
    return <div>{'Could not fetch profile data'}</div>;
  }

  const userResponseData = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
    ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
    { accountId: profileData.aaid as string },
    { fetchPolicy: 'store-or-network' },
  );
  const userData = userResponseData.user;

  const onClickBackToProfile = () => {
    trackBadgesBackToProfileClick(isPersonalView);
  };

  const BackToProfileButton = () => {
    return (
      <Link
        to={`/profile/${pathProfileId}`}
        onClick={onClickBackToProfile}
        css={backLinkStyle}
      >
        {'← Back to profile'}
      </Link>
    );
  };

  const shouldShowLearningBadges =
    hasProfileOwnerConsent && recentCoursesBadges.length > 0;

  return (
    <div data-testid="badges-full-list" css={parentContainer}>
      <div css={contentWrapper}>
        <div
          css={[
            headerAndBadgesContainer,
            !shouldShowLearningBadges && headerAndBadgesContainerNoCourses,
          ]}
        >
          <BackToProfileButton />
          <h2 css={titleStyles}>{`${userData?.nickname}'s badges`}</h2>
          {shouldShowLearningBadges && (
            <MainPanelSectionHeader
              testId="recent-courses-full-list-title"
              title={`${recentCoursesBadges.length} ${
                recentCoursesBadges.length === 1 ? 'Course' : 'Courses'
              }`}
            />
          )}
        </div>

        {shouldShowLearningBadges && (
          <>
            <ul
              data-testid="recent-courses-full-list-container"
              css={badgesContainer}
            >
              {recentCoursesBadges.map((course) => (
                <li key={course.id} css={listItemMargin}>
                  <BadgeCard
                    badgeTitle={course.name || 'Unknown Course'}
                    link={course.actionUrl || ''}
                    image={{
                      src: course.src || 'default_image_path',
                      alt: course.name || 'Course Image',
                    }}
                    isPersonalView={isPersonalView}
                    badgeType="learning"
                  />
                </li>
              ))}
            </ul>
            <div css={sectionSpacer}></div>
          </>
        )}
        {forumsBadges.length > 0 && (
          <>
            <div css={headerAndBadgesContainer}>
              <MainPanelSectionHeader
                testId="recent-badges-full-list-title"
                title={`${forumsBadges.length} ${
                  forumsBadges.length === 1 ? 'Badge' : 'Badges'
                }`}
              />
            </div>
            <ul
              data-testid="recent-badges-full-list-container"
              css={badgesContainer}
            >
              {forumsBadges.map((badge) => (
                <li key={badge.id} css={listItemMargin}>
                  <BadgeCard
                    badgeTitle={badge.name || 'Unknown Badge'}
                    link={badge.actionUrl || ''}
                    image={{
                      src:
                        badge.src ||
                        'https://demofree.sirv.com/nope-not-here.jpg', // In styled version, what do we do if no image?
                      alt: badge.name || 'Course Image',
                    }}
                    isPersonalView={isPersonalView}
                    badgeType="forums"
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

const sectionSpacer = css({
  marginTop: '40px',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    marginTop: '32px',
  },
});

const listItemMargin = css({
  // li by default has margin that must be removed
  margin: '0px',
});

const parentContainer = css({
  display: 'flex',
  justifyContent: 'center',
  minHeight: '550px',
  width: '100%',
});

const contentWrapper = css({
  marginTop: token('space.500'),
  width: '1020px',
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: '100%',
  [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
    {
      maxWidth: '720px',
      marginTop: token('space.500'),
      padding: '0px',
    },
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    marginTop: token('space.400'),
    width: '343px',
  },
});

const backLinkStyle = css({
  alignSelf: 'flex-start',
  color: token('color.text.brand'),
  fontFamily: token('font.family.brand.body'),
  fontSize: token('space.200'),
  fontWeight: token('font.weight.semibold'),
  lineHeight: token('space.250'),
});
const headerAndBadgesContainer = css({
  paddingBottom: '24px',
  margin: '0 auto',
  maxWidth: 'calc(972px + 48px)',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    paddingBottom: '16px',
  },
});

const headerAndBadgesContainerNoCourses = css({
  paddingBottom: '0px',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    paddingBottom: '0px',
  },
});

const titleStyles = css({
  marginTop: '40px',
  fontSize: token('space.500'),
  fontWeight: token('font.weight.bold'),
  fontFamily: token('font.family.brand.heading'),
  lineHeight: token('space.600'),
  paddingBottom: '40px',
  borderBottom: `1px solid ${token('color.border')}`,
  marginBottom: '40px',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    paddingBottom: '24px',
    marginTop: '24px',
    marginBottom: '24px',
    fontSize: '32px',
    lineHeight: '40px',
  },
});

const badgesContainer = css({
  listStyleType: 'none',
  padding: 0,
  display: 'grid',
  gap: '24px',
  justifyContent: 'center',
  margin: '0 auto',
  gridTemplateColumns: '1fr',
  [`@media (min-width: ${MOBILE.MAX_WIDTH}px)`]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 498px))',
  },
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    gap: '16px',
  },
});

export default BadgesFullList;
